import React, { useMemo } from 'react';
import {
  Card,
  Typography,
  Box,
  Chip,
  IconButton,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import moment from 'moment';
import { ISyncStatusCardProps } from '../../types/syncStatusCard';

export const SyncStatusCard = ({ data, loading = false, onRefresh }: ISyncStatusCardProps) => {
  const duration = useMemo(() => {
    if (!data?.started_at || !data?.completed_at) return '-';

    const start = new Date(data.started_at).getTime();
    const end = new Date(data.completed_at).getTime();

    const totalSeconds = Math.floor((end - start) / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  }, [data]);

  const statusConfig = useMemo(() => {
    switch (data?.status) {
      case 'FINISHED':
        return { label: 'Finished', color: 'success', icon: <CheckCircleIcon fontSize="small" /> };
      case 'FAILED':
        return { label: 'Failed', color: 'error', icon: <ErrorIcon fontSize="small" /> };
      case 'INPROGRESS':
        return { label: 'Running', color: 'warning', icon: <SyncIcon fontSize="small" /> };
      default:
        return { label: 'Unknown', color: 'default', icon: undefined };
    }
  }, [data]);

  return (
    <Card sx={{ width: 300, p: 1.5 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography fontSize={14} fontWeight={600}>
          Sync Status
        </Typography>

        <IconButton size="small" onClick={onRefresh}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={1}>
          <CircularProgress size={18} />
        </Box>
      ) : (
        <>
          {data?.status === 'INPROGRESS' && <LinearProgress sx={{ mt: 1, mb: 1 }} />}

          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
            <Typography fontSize={12} color="text.secondary">
              Run #{data?.run_id ?? '-'}
            </Typography>

            <Chip
              icon={statusConfig.icon}
              label={statusConfig.label}
              color={statusConfig.color as any}
              size="small"
            />
          </Box>

          <Typography fontSize={12} color="text.secondary" mt={0.5}>
            Start:{' '}
            {data?.started_at ? moment(data.started_at).format('MMMM Do YYYY, h:mm:ss a') : '-'}
          </Typography>
          <Typography fontSize={12} color="text.secondary" mt={0.5}>
            Completed:{' '}
            {data?.completed_at ? moment(data.completed_at).format('MMMM Do YYYY, h:mm:ss a') : '-'}
          </Typography>
          <Typography fontSize={12} color="text.secondary">
            Duration: {duration}
          </Typography>
        </>
      )}
    </Card>
  );
};
